import { ReactComponent as ArrowLeftIcon } from 'assett/icons/arrow_left_icon.svg';
import { ReactComponent as CloseIcon } from 'assett/icons/close_icon.svg';
import { ReactComponent as EyeShowIcon } from 'assett/icons/eye_show_icon.svg';
import FileRenderer from 'components/commons/FileRenderer/FileRenderer';
import {
	resetDocumentBlob,
	resetDownloadState,
} from 'feature/utils/downloaderSlice';
import { useFileOpener } from 'hooks/downloader.hook';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import {
	Box,
	BoxOverflow,
	DialogDesktop,
	IconButton,
	Stack,
	Typography,
} from 'styles';
import { theme } from 'styles/theme/ThemeStyle';
import { bytesToKilobytes, getShortDate } from 'utils/utils';

const DocumentVisualizerPopup = () => {
	const dispatch = useDispatch();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const fileOpener = useFileOpener();
	const { fileName, downloadedDocumentBlob, docType, documentList } =
		useSelector((store: RootState) => store.downloader);

	const blobUrl =
		downloadedDocumentBlob &&
		URL.createObjectURL(downloadedDocumentBlob as unknown as Blob);

	const handleDownloadOther = (uuid: string, fileName: string) => {
		const url = `${process.env.REACT_APP_API_URL}/attachments/other/${uuid}/content`;
		fileOpener({ url, fileName });
	};

	const handleClickBackToList = () => {
		dispatch(resetDocumentBlob());
	};

	const openDialog = () => {
		if (docType === 'other') {
			return true;
		}

		if (downloadedDocumentBlob) {
			return true;
		}

		return false;
	};

	return (
		<DialogDesktop
			close={
				<IconButton
					onClick={() => {
						dispatch(resetDownloadState());
					}}
				>
					<CloseIcon />
				</IconButton>
			}
			scroll="paper"
			fullWidth
			actions={<></>}
			PaperProps={{
				sx: {
					minHeight: {
						sm: '658px',
						md: '658px',
						lg: '865px',
					},
					maxHeight: {
						sm: '658px',
						md: '658px',
						lg: '865px',
					},
					minWidth: {
						sm: '768px',
						md: '768px',
						lg: '840px',
					},
				},
			}}
			open={openDialog()}
			title={tClientLabels(`newClaim.upload.docNames.${docType}`)}
		>
			{!downloadedDocumentBlob && (
				<Box
					component="ul"
					sx={{
						maxHeight: {
							sm: '550px',
							md: '550px',
							lg: '715px',
						},

						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						gap: '15px',
						p: {
							sm: '20px',
							md: '20px',
							lg: '32px',
						},
						m: 0,
					}}
				>
					{documentList.map(doc => (
						<Stack
							key={doc.uuid}
							direction={'row'}
							justifyContent={'space-between'}
							alignItems={'center'}
							bgcolor={theme => theme.palette.grey6.main}
							paddingY={{
								sm: '14px',
								md: '12px',
							}}
							paddingX={'20px'}
						>
							<Stack gap="15px">
								<Typography variant="bodyLink" sx={{ textDecoration: 'underline' }}>
									{doc.fileName}
								</Typography>
								<Typography variant="body">
									{tClientLabels('newClaim.upload.doc.caption', {
										docDate: getShortDate(doc.lastInsertTs),
										docSize: bytesToKilobytes(Number(doc.size))[0],
										docSizeUnit: bytesToKilobytes(Number(doc.size))[1],
									})}
								</Typography>
							</Stack>
							<IconButton
								color="inherit"
								onClick={() => handleDownloadOther(doc.uuid, doc.fileName!)}
							>
								<EyeShowIcon />
							</IconButton>
						</Stack>
					))}
				</Box>
			)}
			{downloadedDocumentBlob && (
				<Stack
					px={'20px'}
					py={{
						sm: '14px',
						md: '14px',
						lg: '20px',
					}}
				>
					<Stack
						direction={'row'}
						justifyContent={'space-between'}
						py={'20px'}
						position={'absolute'}
						top={{ lg: '63px', md: '53px', sm: '53px' }}
						width={{
							sm: '728px',
							md: '728px',
							lg: '800px',
						}}
						zIndex={10}
						bgcolor={theme.palette.white.main}
						borderTop={`1px solid ${theme.palette.grey3.main}`}
					>
						<Box width={'84px'}>
							{docType === 'other' && (
								<IconButton color="inherit" onClick={handleClickBackToList}>
									<ArrowLeftIcon />
								</IconButton>
							)}
						</Box>
						<Typography variant="bodyLink">{fileName}</Typography>
						<Box width={'84px'} />
					</Stack>
					{downloadedDocumentBlob && (
						<BoxOverflow
							variant="y"
							sx={{
								marginTop: {
									sm: '50px',
									md: '50px',
									lg: '44px',
								},
								maxHeight: {
									sm: '468px',
									md: '468px',
									lg: '650px',
								},
								border: `1px solid ${theme.palette.grey5.main}`,
							}}
						>
							<FileRenderer
								mime={downloadedDocumentBlob.type}
								url={blobUrl!}
								name={fileName}
								isBlob
							/>
						</BoxOverflow>
					)}
				</Stack>
			)}
		</DialogDesktop>
	);
};

export default DocumentVisualizerPopup;
